import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Cartao } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { get } from '../../nucleo/api';
import { data, dinheiro, numero } from '../../nucleo/formato';
import { Barra, BotaoExportar, Kpi, LinhaEscopo, NomeParceiro, Rolagem, TD, TH, Valor } from './comum';
import type { Mrr as MrrApi } from './tipos';

/** Periodicidades de nome fixo que o MRR devolve (recorrências e contratos). */
const PERIODICIDADES = ['mensal', 'bimestral', 'trimestral', 'semestral', 'anual', 'semanal', 'diaria'] as const;
type Periodicidade = (typeof PERIODICIDADES)[number];

/**
 * Periodicidade do MRR no idioma do app. Review r16: o MRR por contrato devolve também `bimestral`, `semestral` e `meses_<n>`
 * ("meses_4" vira "A cada 4 meses", com plural); o que não for reconhecido volta como veio.
 */
export function rotuloPeriodicidade(p: string, t: TFunction<'relatorios'>): string {
  if ((PERIODICIDADES as readonly string[]).includes(p)) return t(`mrr.periodicidades.${p as Periodicidade}`);
  // C4: contratos por semana ou por dia (`semanas_<n>`, `dias_<n>`).
  const n = /^(meses|semanas|dias)_(\d{1,3})$/.exec(p);
  return n ? t(`mrr.periodicidades.${n[1] as 'meses' | 'semanas' | 'dias'}`, { count: Number(n[2]) }) : p;
}

/** R3 · MRR: estimado pelas recorrências (v0) ou por contrato, quando o módulo de Contratos estiver instalado. */
export function Mrr() {
  const { t } = useTranslation('relatorios');
  const q = useQuery({ queryKey: ['/relatorios/mrr'], queryFn: () => get<MrrApi>('/relatorios/mrr'), placeholderData: keepPreviousData });
  const periodicidade = (p: string) => rotuloPeriodicidade(p, t);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Barra>
        <BotaoExportar relatorio="mrr" parametros={{}} />
      </Barra>
      <Estado consulta={q}>
        {(d) => (
          <>
            <div>
              <h2 className="text-base font-semibold text-tinta-1">{d.fonte === 'contratos' ? t('mrr.tituloContratos') : t('mrr.tituloRecorrencias')}</h2>
              <LinhaEscopo escopo={d.escopo} extra={<span>{`. ${t('mrr.referencia', { data: data(d.em) })}`}</span>} />
            </div>
            {d.fonte === 'recorrencias' && <p className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2">{t('mrr.avisoRecorrencias')}</p>}
            {d.por_moeda.length === 0 ? (
              <Cartao>
                <Vazio titulo={t('mrr.vazioTitulo')} texto={t('mrr.vazioTexto')} />
              </Cartao>
            ) : (
              d.por_moeda.map((m) => (
                <section key={m.moeda} aria-label={t('comum.moeda', { moeda: m.moeda })} className="flex min-w-0 flex-col gap-4">
                  {d.por_moeda.length > 1 && <h2 className="text-sm font-semibold">{t('comum.moeda', { moeda: m.moeda })}</h2>}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
                    <Kpi rotulo={t('mrr.mrr')} cor="text-verde" valor={dinheiro(m.mrr_centavos, m.moeda)} />
                    <Kpi rotulo={t('mrr.arr')} valor={dinheiro(m.arr_centavos, m.moeda)} />
                    <Kpi rotulo={t('mrr.ativos')} valor={numero(m.ativos)} />
                    <Kpi rotulo={t('mrr.pausado')} cor="text-laranja" valor={dinheiro(m.pausado_centavos, m.moeda)} />
                    <Kpi rotulo={t('mrr.aIniciar')} cor="text-ciano" valor={dinheiro(m.a_iniciar_centavos, m.moeda)} />
                  </div>
                  <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-2">
                    <Cartao titulo={t('mrr.porPeriodicidade')} semPadding>
                      <Rolagem rotulo={`${t('mrr.porPeriodicidade')} (${m.moeda})`}>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-borda text-left text-tinta-3">
                              <th scope="col" className={TH}>{t('mrr.periodicidade')}</th>
                              <th scope="col" className={`${TH} text-right`}>{t('mrr.ativos')}</th>
                              <th scope="col" className={`${TH} text-right`}>{t('mrr.mrr')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.por_periodicidade.map((p) => (
                              <tr key={p.periodicidade} className="border-b border-borda last:border-0">
                                <th scope="row" className={`${TD} text-left font-normal`}>{periodicidade(p.periodicidade)}</th>
                                <td className={`${TD} numero text-right`}>{p.ativos}</td>
                                <td className={`${TD} text-right`}>
                                  <Valor centavos={p.mrr_centavos} moeda={m.moeda} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Rolagem>
                    </Cartao>
                    <Cartao titulo={t('mrr.porCliente')} semPadding>
                      <Rolagem rotulo={`${t('mrr.porCliente')} (${m.moeda})`}>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-borda text-left text-tinta-3">
                              <th scope="col" className={TH}>{t('mrr.cliente')}</th>
                              <th scope="col" className={`${TH} text-right`}>{t('mrr.ativos')}</th>
                              <th scope="col" className={`${TH} text-right`}>{t('mrr.mrr')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.por_cliente.map((c) => (
                              <tr key={c.parceiro_id ?? 'sem'} className="border-b border-borda last:border-0">
                                <th scope="row" className={`${TD} max-w-64 text-left font-normal break-words`}>
                                  <NomeParceiro nome={c.nome} vazio={t('comum.semNome')} />
                                </th>
                                <td className={`${TD} numero text-right`}>{c.ativos}</td>
                                <td className={`${TD} text-right`}>
                                  <Valor centavos={c.mrr_centavos} moeda={m.moeda} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Rolagem>
                    </Cartao>
                  </div>
                  {d.escopo.modo === 'consolidado' && m.por_empresa.length > 0 && (
                    <Cartao titulo={t('mrr.porEmpresa')} semPadding>
                      <Rolagem rotulo={`${t('mrr.porEmpresa')} (${m.moeda})`}>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-borda text-left text-tinta-3">
                              <th scope="col" className={TH}>{t('mrr.empresa')}</th>
                              <th scope="col" className={`${TH} text-right`}>{t('mrr.mrr')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.por_empresa.map((e) => (
                              <tr key={e.empresa_id ?? 'sem'} className="border-b border-borda last:border-0">
                                <th scope="row" className={`${TD} text-left font-normal`}>{d.escopo.empresas.find((x) => x.id === e.empresa_id)?.nome ?? ''}</th>
                                <td className={`${TD} text-right`}>
                                  <Valor centavos={e.mrr_centavos} moeda={m.moeda} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Rolagem>
                    </Cartao>
                  )}
                </section>
              ))
            )}
          </>
        )}
      </Estado>
    </div>
  );
}
